import { PermissionPayload } from "@/modules/auth";
import { ClientePayload } from "@/modules/business/cliente/clienteBusiness";
import { format, formatNumber } from "@/modules/utils";

/**
 * Tipos, flags e regras de tela do módulo RNTRC (ANTT).
 *
 * As flags espelham os enums do aet-api (commons/enums/rntrc). As datas chegam como Date:
 * o interceptor de resposta da API converte todo campo iniciado por "data"/"timestamp".
 */

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

export type TipoServicoRntrc =
  | "IMF"
  | "IAR"
  | "EXC"
  | "TRF"
  | "CPF"
  | "CPJ"
  | "REA"
  | "ALT"
  | "CTR"
  | "CVE"
  | "RPE";

export type SituacaoRntrc =
  | "AGR"
  | "PRO"
  | "PAG"
  | "PGI"
  | "CON"
  | "ERR"
  | "CAN"
  | "PSC"
  | "TSC"
  | "CRD"
  | "CIR"
  | "EXN"
  | "AEX";

export type ServicoVeiculoRntrc = "AUT" | "IMP";
export type UnidadePrecoRntrc = "PLA" | "SOL";
export type StatusItemFaturamentoRntrc = "FAT" | "BON" | "CAN" | "NRE";
export type MotivoCancelamentoRntrc = "CLI" | "PIX" | "ROB";
/** Pedido do cliente ao robô sobre o pedido aberto na ANTT: CPE pedido inteiro, CPL placas. */
export type AcaoRoboRntrc = "CPE" | "CPL";
export type CodigoErroRntrc = "REJ" | "LOG" | "TIM" | "SIS";
export type DocumentoRntrc = "EXT" | "CER" | "REL" | "COM" | "ERR";
export type FilaRntrc = "SOL" | "MON";

/** Cor do badge (classes .aet-badge--* de scss/utilities.scss). */
export type BadgeCor = "info" | "warning" | "success" | "danger" | "muted";

export interface FlagOption<T extends string> {
  flag: T;
  descricao: string;
}

export const TIPOS_SERVICO_RNTRC: Array<FlagOption<TipoServicoRntrc>> = [
  { flag: "IMF", descricao: "Inclusão de veículo próprio" },
  { flag: "IAR", descricao: "Inclusão de veículo arrendado" },
  { flag: "EXC", descricao: "Exclusão de veículo" },
  { flag: "TRF", descricao: "Transferência de veículo" },
  { flag: "CPF", descricao: "Novo cadastro TAC (Pessoa Física)" },
  { flag: "CPJ", descricao: "Novo cadastro ETC (Pessoa Jurídica)" },
  { flag: "REA", descricao: "Reativação de cadastro" },
  { flag: "ALT", descricao: "Alteração de dados do veículo" },
  { flag: "CTR", descricao: "Consulta de transportador (CPF/CNPJ)" },
  { flag: "CVE", descricao: "Consulta de veículo" },
  { flag: "RPE", descricao: "Relatório de pendências" }
];

export const SITUACOES_RNTRC: Array<FlagOption<SituacaoRntrc> & {
  cor: BadgeCor;
}> = [
  { flag: "AGR", descricao: "Aguardando robô", cor: "info" },
  { flag: "PRO", descricao: "Processando", cor: "info" },
  { flag: "PAG", descricao: "Aguardando pagamento", cor: "warning" },
  { flag: "PGI", descricao: "Pagamento informado", cor: "warning" },
  { flag: "CON", descricao: "Concluído", cor: "success" },
  { flag: "ERR", descricao: "Erro", cor: "danger" },
  { flag: "CAN", descricao: "Cancelado", cor: "muted" },
  { flag: "PSC", descricao: "Placa sem cadastro no RNTRC", cor: "muted" },
  {
    flag: "TSC",
    descricao: "Transportador sem cadastro no RNTRC",
    cor: "muted"
  },
  {
    flag: "CRD",
    descricao: "CRLV desatualizado — divergente do SENATRAN",
    cor: "muted"
  },
  {
    flag: "CIR",
    descricao: "CNPJ não cadastrado ou inativo na Receita Federal",
    cor: "muted"
  },
  { flag: "EXN", descricao: "Exclusão necessária", cor: "warning" },
  { flag: "AEX", descricao: "Excluindo da frota atual", cor: "info" }
];

export const SERVICOS_VEICULO_RNTRC: Array<FlagOption<ServicoVeiculoRntrc>> = [
  { flag: "AUT", descricao: "Tração" },
  { flag: "IMP", descricao: "Tracionado" }
];

export const UNIDADES_PRECO_RNTRC: Array<FlagOption<UnidadePrecoRntrc>> = [
  { flag: "PLA", descricao: "Por placa" },
  { flag: "SOL", descricao: "Por solicitação" }
];

export const STATUS_ITEM_FATURAMENTO_RNTRC: Array<FlagOption<
  StatusItemFaturamentoRntrc
> & { cor: BadgeCor }> = [
  { flag: "FAT", descricao: "Faturável", cor: "success" },
  { flag: "BON", descricao: "Bonificado", cor: "info" },
  { flag: "CAN", descricao: "Cancelado", cor: "muted" },
  { flag: "NRE", descricao: "Não realizado", cor: "muted" }
];

export const MOTIVOS_CANCELAMENTO_RNTRC: Array<FlagOption<
  MotivoCancelamentoRntrc
>> = [
  { flag: "CLI", descricao: "Cancelada pelo cliente" },
  { flag: "PIX", descricao: "PIX venceu sem pagamento" },
  { flag: "ROB", descricao: "Recusa definitiva da ANTT" }
];

export const CODIGOS_ERRO_RNTRC: Array<FlagOption<CodigoErroRntrc>> = [
  { flag: "REJ", descricao: "Rejeição do site" },
  { flag: "LOG", descricao: "Erro de login" },
  { flag: "TIM", descricao: "Timeout" },
  { flag: "SIS", descricao: "Erro de sistema" }
];

export const DOCUMENTOS_RNTRC: Array<FlagOption<DocumentoRntrc>> = [
  { flag: "EXT", descricao: "Extrato" },
  { flag: "CER", descricao: "Certificado" },
  { flag: "REL", descricao: "Relatório de pendências" },
  { flag: "COM", descricao: "Comprovante" },
  { flag: "ERR", descricao: "Imagem do erro" }
];

/** Situações em que o robô ainda vai mexer na solicitação (a tela se atualiza sozinha). */
export const SITUACOES_EM_ANDAMENTO: Array<SituacaoRntrc> = [
  "AGR",
  "PRO",
  "PAG",
  "PGI",
  "AEX"
];

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export interface RntrcPrecoServicoPayload {
  idRntrcPrecoServico?: number | null;
  idCliente?: number | null;
  cliente?: ClientePayload | null;
  flTipoServico?: TipoServicoRntrc | null;
  flServicoVeiculo?: ServicoVeiculoRntrc | null;
  flUnidade?: UnidadePrecoRntrc | null;
  valor?: number | null;
  dataInicioVigencia?: Date | null;
  dataFimVigencia?: Date | null;
  permissionInfo?: PermissionPayload;
}

export interface RntrcTipoServicoPayload {
  flTipoServico: TipoServicoRntrc;
  mensagem?: string | null;
  pago?: boolean | null;
  comVeiculos?: boolean | null;
  exigeCrlv?: boolean | null;
  exigeCpfCnpj?: boolean | null;
  precos?: Array<RntrcPrecoServicoPayload> | null;
}

export interface RntrcSolicitacaoVeiculoPayload {
  idRntrcSolicitacaoVeiculo?: number | null;
  /** Veículo do cadastro da ANTT; nulo quando a placa foi digitada (EXC, CVE). */
  idVeiculoAntt?: number | null;
  ordem?: number | null;
  placa?: string | null;
  renavam?: string | null;
  chassi?: string | null;
  flServicoVeiculo?: ServicoVeiculoRntrc | null;
  cpfCnpjArrendante?: string | null;
  nomeArrendante?: string | null;
  possuiCrlv?: boolean | null;
  possuiContrato?: boolean | null;
  /** IAR: upload novo do contrato de arrendamento (key do storage temporário). */
  keyBlContratoTemp?: string | null;
  /** CVE: documentos do transportador atual desta placa (download por veículo). */
  possuiExtrato?: boolean | null;
  possuiCertificado?: boolean | null;
  flIncluido?: boolean | null;
  motivo?: string | null;
  servicoSite?: string | null;
  valorSite?: number | null;
  valorServico?: number | null;
  /** Cancelamento na ANTT antes de pagar: pedido pelo cliente, feito pelo robô. */
  flCancelamentoSolicitado?: boolean | null;
  dataCancelamento?: Date | null;
  /** Inclusão: placa presa em contrato de outro transportador (com quem está em cpfCnpjAtual). */
  flExclusaoNecessaria?: boolean | null;
  cpfCnpjAtual?: string | null;
  nomeTransportadorAtual?: string | null;
  rntrcAtual?: string | null;
  situacaoAtual?: string | null;
  /** Consulta de veículo: o veículo como está no extrato do transportador atual (robô). */
  renavamAtual?: string | null;
  flServicoVeiculoAtual?: ServicoVeiculoRntrc | null;
  categoriaAtual?: string | null;
  siglaUfAtual?: string | null;
  propriedadeAtual?: string | null;
  permissionInfo?: PermissionPayload;
}

export interface RntrcSolicitacaoPayload {
  idRntrcSolicitacao?: number | null;
  cliente?: ClientePayload | null;
  /** Pessoa do pedido no cadastro da ANTT (transportador; arrendatário no IAR). */
  idPessoaAntt?: number | null;
  nomeUsuario?: string | null;
  flTipoServico?: TipoServicoRntrc | null;
  flSituacao?: SituacaoRntrc | null;
  cpfCnpj?: string | null;
  nomeTransportador?: string | null;
  /** Novo cadastro: titular da CNH — gestor (sócio) no ETC, motorista no TAC. */
  cnhCpf?: string | null;
  cnhNome?: string | null;
  /** yyyy-mm-dd */
  cnhDataNascimento?: string | null;
  cnhNumero?: string | null;
  cnhCategoria?: string | null;
  cnhSexo?: string | null;
  /** CTR: lidos do cadastro da ANTT pelo robô. */
  nomeRntrcTransportador?: string | null;
  rntrcTransportador?: string | null;
  situacaoRntrcTransportador?: string | null;
  cpfCnpjDestino?: string | null;
  flProsseguirParcial?: boolean | null;
  idPedidoSitcarga?: string | null;
  tipoUrlPedido?: string | null;
  urlPedidoSitcarga?: string | null;
  codigoPix?: string | null;
  valorPix?: number | null;
  dataPix?: Date | null;
  dataVencimentoPix?: Date | null;
  dataPagamentoInformado?: Date | null;
  dataPagamentoConfirmado?: Date | null;
  dataConclusao?: Date | null;
  dataCancelamento?: Date | null;
  flMotivoCancelamento?: MotivoCancelamentoRntrc | null;
  /** Cancelamento na ANTT em andamento (o robô ainda vai executar). */
  flAcaoRobo?: AcaoRoboRntrc | null;
  /** Exclusão da frota atual: a inclusão que a pediu. */
  idRntrcSolicitacaoPai?: number | null;
  /** Inclusão: as exclusões da frota atual criadas por ela (PIX e situação). */
  exclusoes?: Array<RntrcSolicitacaoPayload> | null;
  protocolo?: string | null;
  possuiExtrato?: boolean | null;
  possuiCertificado?: boolean | null;
  possuiRelatorio?: boolean | null;
  possuiComprovante?: boolean | null;
  possuiImagemErro?: boolean | null;
  codigoErro?: CodigoErroRntrc | null;
  msgCliente?: string | null;
  logErro?: string | null;
  valorServico?: number | null;
  valorTotalServico?: number | null;
  quantidadeVeiculos?: number | null;
  placas?: string | null;
  proximaVerificacaoPagamento?: Date | null;
  statusRobo?: string | null;
  tentativasRobo?: number | null;
  observacao?: string | null;
  cienteAvisos?: boolean | null;
  rg?: string | null;
  orgaoEmissorIdentidade?: string | null;
  ufIdentidade?: string | null;
  enderecoTipo?: string | null;
  enderecoCep?: string | null;
  enderecoLogradouro?: string | null;
  enderecoNumero?: string | null;
  enderecoBairro?: string | null;
  contatoTelefone?: string | null;
  contatoEmail?: string | null;
  dataCadastro?: Date | null;
  dataAlteracao?: Date | null;
  veiculos?: Array<RntrcSolicitacaoVeiculoPayload> | null;
  permissionInfo?: PermissionPayload;
}

export interface RntrcSolicitacaoFilterPayload {
  idCliente?: number | null;
  idPessoaAntt?: number | null;
  idRntrcSolicitacao?: number | null;
  flTipoServico?: TipoServicoRntrc | null;
  situacoes: Array<SituacaoRntrc>;
  placa?: string | null;
  cpfCnpj?: string | null;
  idPedidoSitcarga?: string | null;
  dataInicio?: Date | null;
  dataFim?: Date | null;
}

export interface RntrcFaturamentoItemPayload {
  idRntrcFaturamentoItem?: number | null;
  idCliente?: number | null;
  nomeCliente?: string | null;
  idRntrcSolicitacao?: number | null;
  idRntrcSolicitacaoVeiculo?: number | null;
  flTipoServico?: TipoServicoRntrc | null;
  flServicoVeiculo?: ServicoVeiculoRntrc | null;
  flStatusItem?: StatusItemFaturamentoRntrc | null;
  flUnidade?: UnidadePrecoRntrc | null;
  valor?: number | null;
  valorSite?: number | null;
  placa?: string | null;
  cpfCnpj?: string | null;
  nomeTransportador?: string | null;
  idPedidoSitcarga?: string | null;
  dataSolicitacao?: Date | null;
  dataPagamentoPix?: Date | null;
  dataConclusao?: Date | null;
  dataCancelamento?: Date | null;
  dataCompetencia?: Date | null;
}

export interface RntrcFaturamentoResumoPayload {
  idCliente?: number | null;
  nomeCliente?: string | null;
  flTipoServico?: TipoServicoRntrc | null;
  flStatusItem?: StatusItemFaturamentoRntrc | null;
  quantidade?: number | null;
  valorTotal?: number | null;
}

export interface RntrcFaturamentoFilterPayload {
  /** yyyy-MM */
  competencia?: string | null;
  idCliente?: number | null;
  flTipoServico?: TipoServicoRntrc | null;
  flStatusItem?: StatusItemFaturamentoRntrc | null;
  placa?: string | null;
}

export interface RntrcFilaPainelPayload {
  fila: FilaRntrc;
  idFila: number;
  idRntrcSolicitacao?: number | null;
  idCliente?: number | null;
  nomeCliente?: string | null;
  flTipoServico?: TipoServicoRntrc | null;
  flSituacao?: SituacaoRntrc | null;
  cpfCnpj?: string | null;
  statusRobo?: string | null;
  tentativas?: number | null;
  prioridade?: boolean | null;
  timestampUltimaExecucao?: Date | null;
  timestampProximaExecucao?: Date | null;
  nomeRobo?: string | null;
  logErro?: string | null;
  ultimaSituacaoSite?: string | null;
  dataCadastro?: Date | null;
}

// ---------------------------------------------------------------------------
// Rótulos
// ---------------------------------------------------------------------------

const descricao = <T extends string>(
  lista: Array<FlagOption<T>>,
  flag?: string | null
): string => {
  if (!flag) {
    return "";
  }
  return lista.find((e) => e.flag === flag)?.descricao ?? flag;
};

export const getTipoServicoLabel = (flag?: string | null) =>
  descricao(TIPOS_SERVICO_RNTRC, flag);

export const getSituacaoLabel = (flag?: string | null) =>
  descricao(SITUACOES_RNTRC, flag);

export const getSituacaoCor = (flag?: string | null): BadgeCor =>
  SITUACOES_RNTRC.find((e) => e.flag === flag)?.cor ?? "muted";

export const getServicoVeiculoLabel = (flag?: string | null) =>
  descricao(SERVICOS_VEICULO_RNTRC, flag);

export const getUnidadePrecoLabel = (flag?: string | null) =>
  descricao(UNIDADES_PRECO_RNTRC, flag);

export const getStatusItemLabel = (flag?: string | null) =>
  descricao(STATUS_ITEM_FATURAMENTO_RNTRC, flag);

export const getStatusItemCor = (flag?: string | null): BadgeCor =>
  STATUS_ITEM_FATURAMENTO_RNTRC.find((e) => e.flag === flag)?.cor ?? "muted";

export const getMotivoCancelamentoLabel = (flag?: string | null) =>
  descricao(MOTIVOS_CANCELAMENTO_RNTRC, flag);

export const getCodigoErroLabel = (flag?: string | null) =>
  descricao(CODIGOS_ERRO_RNTRC, flag);

export const getDocumentoLabel = (flag?: string | null) =>
  descricao(DOCUMENTOS_RNTRC, flag);

export const isEmAndamento = (flag?: string | null) =>
  !!flag && SITUACOES_EM_ANDAMENTO.indexOf(flag as SituacaoRntrc) >= 0;

/** Tipos com lista de veículos na solicitação. */
export const tipoComVeiculos = (tipo?: string | null) =>
  ["IMF", "IAR", "EXC", "TRF", "ALT", "CVE"].indexOf(tipo ?? "") >= 0;

/** Tipos em que o SITCarga gera PIX para o transportador pagar. */
export const tipoPago = (tipo?: string | null) =>
  ["IMF", "IAR", "EXC", "TRF", "ALT"].indexOf(tipo ?? "") >= 0;

// ---------------------------------------------------------------------------
// Formatação
// ---------------------------------------------------------------------------

export const somenteDigitos = (v?: string | null) =>
  (v ?? "").replace(/\D/g, "");

export const normalizarPlaca = (v?: string | null) =>
  (v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

export const formatCpfCnpj = (v?: string | null) => {
  const d = somenteDigitos(v);
  if (!d) {
    return "";
  }
  return format(["###.###.###-##", "##.###.###/####-##"], d);
};

export const formatPlaca = (v?: string | null) => {
  const p = normalizarPlaca(v);
  // placa antiga com hífen; Mercosul sem
  return /^[A-Z]{3}[0-9]{4}$/.test(p) ? p.substr(0, 3) + "-" + p.substr(3) : p;
};

export const formatMoeda = (v?: number | null) => {
  if (v === null || typeof v === "undefined") {
    return "";
  }
  return formatNumber(v, { style: "currency", currency: "BRL" });
};

/** Date -> "yyyy-MM" no fuso local (competência). */
export const toCompetencia = (d?: Date | null) => {
  if (!d) {
    return null;
  }
  return (
    d.getFullYear() +
    "-" +
    (d.getMonth() + 1 < 10 ? "0" : "") +
    (d.getMonth() + 1)
  );
};

/** Tempo restante até a data ("3h 12min", "12min", "vencido"). */
export const formatTempoRestante = (alvo?: Date | null, agora?: Date) => {
  if (!alvo) {
    return "";
  }
  const diff = alvo.getTime() - (agora ?? new Date()).getTime();
  if (diff <= 0) {
    return "vencido";
  }
  const totalMin = Math.floor(diff / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) {
    return h + "h " + (m < 10 ? "0" : "") + m + "min";
  }
  if (totalMin > 0) {
    return totalMin + "min";
  }
  return Math.floor(diff / 1000) + "s";
};

// ---------------------------------------------------------------------------
// Validação (espelha o RntrcSolicitacaoValidator do aet-api)
// ---------------------------------------------------------------------------

export const isCpfValido = (v?: string | null) => {
  const cpf = somenteDigitos(v);
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) {
    return false;
  }
  const digito = (base: string, pesoInicial: number) => {
    let soma = 0;
    for (let i = 0; i < base.length; i++) {
      soma += +base.charAt(i) * (pesoInicial - i);
    }
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  const d1 = digito(cpf.substr(0, 9), 10);
  const d2 = digito(cpf.substr(0, 10), 11);
  return d1 === +cpf.charAt(9) && d2 === +cpf.charAt(10);
};

export const isCnpjValido = (v?: string | null) => {
  const cnpj = somenteDigitos(v);
  if (cnpj.length !== 14 || /^(\d)\1{13}$/.test(cnpj)) {
    return false;
  }
  const digito = (base: string) => {
    let soma = 0;
    let peso = 2;
    for (let i = base.length - 1; i >= 0; i--) {
      soma += +base.charAt(i) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  const d1 = digito(cnpj.substr(0, 12));
  const d2 = digito(cnpj.substr(0, 13));
  return d1 === +cnpj.charAt(12) && d2 === +cnpj.charAt(13);
};

export const isCpfCnpjValido = (v?: string | null) => {
  const d = somenteDigitos(v);
  return d.length === 11 ? isCpfValido(d) : isCnpjValido(d);
};

/** Placa antiga (AAA9999) ou Mercosul (AAA9A99). */
export const isPlacaValida = (v?: string | null) =>
  /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(normalizarPlaca(v));

export const isRenavamValido = (v?: string | null) =>
  /^[0-9]{11}$/.test(somenteDigitos(v));
